import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { CornerDownLeft, X } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import type { ParkingNote } from '@/lib/focus/focusMachine';

interface ParkingLotProps {
  notes: ParkingNote[];
  onAdd: (text: string) => void;
  onRemove: (id: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Jot down the stray thought ("reply to Riya") without leaving the block.
 * The classic Pomodoro trick: writing it down is what lets you stop thinking
 * about it. The list scrolls after a handful, following the newest, so the
 * panel (and the Navbar's) stays the same height however many pile up. At
 * the end of the session the summary hands them back.
 */
export function ParkingLot({
  notes,
  onAdd,
  onRemove,
  placeholder = 'Park a stray thought…',
  className,
}: ParkingLotProps) {
  const inputId = useId();
  const [text, setText] = useState('');
  const listRef = useRef<HTMLUListElement>(null);

  // Keep the newest thought in view when the list scrolls.
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [notes.length]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    onAdd(text);
    setText('');
  };

  return (
    <div className={cn('space-y-2', className)}>
      <form onSubmit={submit} className='relative'>
        <label htmlFor={inputId} className='sr-only'>
          Park a stray thought
        </label>
        <input
          id={inputId}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={placeholder}
          className='h-9 w-full rounded-md border border-input bg-background pl-3 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11 max-sm:pr-12 max-sm:text-base'
        />
        <button
          type='submit'
          aria-label='Park it'
          className='absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:right-0 max-sm:top-0 max-sm:h-11 max-sm:w-11'
        >
          <CornerDownLeft className='h-4 w-4' aria-hidden='true' />
        </button>
      </form>
      {notes.length > 0 && (
        <ul
          ref={listRef}
          // Reachable by keyboard through its remove buttons, so it needs no
          // tab stop of its own.
          className='max-h-40 space-y-1 overflow-y-auto overscroll-contain rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          aria-label='Parked thoughts'
        >
          {notes.map(note => (
            <li
              key={note.id}
              className='group flex items-center justify-between gap-2 rounded-md px-2 py-1 text-sm text-foreground hover:bg-secondary max-sm:min-h-11 max-sm:py-0'
            >
              <span className='min-w-0 truncate'>· {note.text}</span>
              <button
                type='button'
                onClick={() => onRemove(note.id)}
                aria-label={`Remove: ${note.text}`}
                className='tap-target shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              >
                <X className='h-3.5 w-3.5' aria-hidden='true' />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
